import type { LoadedApp } from '@/features/page-editor/store/types'
import type { RailItemId } from '@/shell/ide-v2/types'
import type { StudioAuthorityDomain } from '@/shell/ide-v2/studio-authority-workspaces'
import type { IdeSelectionContext } from '@/shell/ide-v2/selection-context'
import { loadTemplate, loadTemplateWithBundle, getTemplateCatalog, type TemplateSummary } from '@/features/page-editor/services/template-loader'
import { deriveStarterTemplateLineage } from '@/features/templates/starter-lineage'
import * as api from '@/services/project-api'
import type { CreateProjectOptions, ProjectMeta } from '@/services/project-api'
import { dropProjectBlockBaselines } from '@/services/block-baselines'
import { flushSaves } from '@/store/auto-save'
import { flushEditorFullPersist } from '@/store/auto-save-bridge'
import { preparePublicationCandidate } from '@/features/publishing/publication-release-api'
import type { PublicationReleaseProjection } from '@/features/publishing/publication-release-types'
// The flow-editor store is project-scoped: every project open/close path
// below calls `ensureProject` so the previous project's flows, drafts, and
// editing buffer never leak into the next project (where one edit would
// persist them into ITS blocks via the auto-save bridge's first-block
// fallback). Safe static import — the flow-editor store's module graph does
// not reach back into studio-store.
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
// Creator copy for "this project will not open". The strings these replaced
// described our storage layer in English only; see the module header for what
// they failed to tell the reader.
import { projectLoadErrorMessage } from '@/store/project-load-error-copy'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import { COOGO_IDE_CHROME_COPY } from '@/shell/ide-v2/coogo-ide-chrome-copy'
import { toast } from '@/store/toast-store'
import { ProjectCreateError } from '@/store/project-create-error'
import { TEMPLATE_STORIES } from '@/features/story/data'
import type { Story } from '@/store/story-types'
import type { DesignSystemState } from '@/features/design-system/lib/design-system-options'
import {
  computeThemeTokens,
  parseDesignSystemState,
} from '@/features/design-system/lib/theme-tokens'
import {
  readProjectDesignSystemRegistrationFromSettings,
} from '@/features/design-system/lib/project-design-system-generation-binding'
import type { OpenPencilFigmaSourceBundle } from '@/features/openpencil-figma/openpencil-figma-source'
import type { OpenPencilSourceWorkspace } from '@/features/openpencil-figma/openpencil-source-workspace'
import {
  applyStudioEndUserBrowserEvidenceToProof,
  attachStudioGenerationEvidenceToApp,
  buildStudioEndUserAppGenerationProof,
  buildStudioGenerationEvidence,
  withStudioEndUserAppGenerationProof,
  type StudioEndUserAppBrowserEvidence,
} from '@/features/validation/studio-generation-evidence'
import { withActionFlowBrowserTraceReadiness } from '@/features/preview/services/actionflow-browser-readiness'
import { clearSessionOnlyDesignMutationAuthority } from '@/features/openpencil-figma/openpencil-design-mutation-guard'
import type { ActionFlowTraceEntry } from '@/features/preview/types'
import {
  withApplicationGenerationQualityFromProof,
  type ApplicationGenerationQualitySource,
} from '@/features/chat/services/application-responsibility'
import {
  deriveStudioProjectPlanFromMeta,
  readStudioProjectPlan,
  type StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'
import {
  exportLoadedAppAsCogoApplication,
  normalizeCogoApplicationBundle,
  type CogoApplicationBundle,
} from '@/features/project/lib/cogo-application-io'
import type { Breakpoint } from '@/features/page-editor/store/types'
import { breakpointForSourceAppKind } from '@/features/page-editor/lib/responsive'
import {
  generatedAppViewportBreakpoint,
  resolveGeneratedAppViewportMetadata,
} from '@/features/preview/viewport/generated-app-viewport'
import type { ViewportSize } from './types'
import { RECENT_PROJECTS_KEY } from './recent-projects-key'
// Leaf module by construction (see its header): the project-exit release of
// the session surface, kept out of this file so the `project-slice ->
// studio-store` import cycle guarded by
// `store/__tests__/project-slice-circular-import.test.ts` is not widened.
import {
  resetProjectSessionSurfaceOnExit,
  type ProjectSessionSurfaceState,
} from './project-session-exit'
// Store-leaf too: its only non-type local import is type-only, so this does
// not widen the guarded cycle either.
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'
import { manifestWithViewportPolicy } from '@/features/design-system/viewport/declare-viewport-policy'
import type { ViewportPolicy } from '@/features/design-system/viewport/viewport-policy'
import { markProjectContentDirty } from './project-content-dirty'

export type StudioView = 'dashboard' | 'workspace' | 'template-detail'

/** Where the current project came from */
export type ProjectSource = 'template' | 'server' | 'import'

type CreateServerProjectOptions = CreateProjectOptions & {
  navigate?: boolean
  activateWorkspace?: boolean
}

const MAX_RECENT = 10
const IMPORTED_COGO_APPLICATION_DB = 'cogo-studio-imported-cogo-application'
const IMPORTED_COGO_APPLICATION_STORE = 'snapshots'
const latestProjectRename = new Map<string, symbol>()

/**
 * Saved-work read de-duplication (operator-measured, 2026-07-31).
 *
 * `/studio/projects` opened SEVEN concurrent `GET /api/projects` reads: every
 * screen that shows saved work called `loadUserProjects` from its own mount
 * effect, React StrictMode ran each of those twice, and the effects were keyed
 * on the `user` OBJECT — which `onStudioAuthStateChange` replaces with a fresh
 * instance on every auth event, re-firing them again. The Worker serialised
 * the pile-up: the first read returned at 10s, the second at 20s, and the
 * remaining five hit the 30s deadline and aborted, so the screen sat on
 * "Loading saved work…" for half a minute.
 *
 * Requests are shared instead of repeated: concurrent callers await ONE
 * in-flight promise, and a read that already succeeded within
 * `USER_PROJECTS_FRESH_MS` is reused rather than re-issued. Mutations and the
 * user's own retry pass `{ force: true }`, which bypasses both.
 */
const USER_PROJECTS_FRESH_MS = 15_000
/** After a failure, unforced callers back off this long before retrying. */
const USER_PROJECTS_ERROR_COOLDOWN_MS = 3_000

interface UserProjectsLoadState {
  /** Principal the cached read belongs to (`null` = signed out). */
  ownerKey: string | null
  inFlight: Promise<void> | null
  /** Epoch ms of the last successful read; `0` = never. */
  loadedAt: number
  /** Epoch ms of the last failed read; `0` = none since the last success. */
  erroredAt: number
}

let userProjectsLoad: UserProjectsLoadState = {
  ownerKey: null,
  inFlight: null,
  loadedAt: 0,
  erroredAt: 0,
}

/**
 * Drop the shared-read state between tests. Module scope survives
 * `useStudioStore.setState`, so without this a test's cached read would
 * silently satisfy the next test's first call.
 */
export function resetUserProjectsLoadStateForTests(): void {
  userProjectsLoad = { ownerKey: null, inFlight: null, loadedAt: 0, erroredAt: 0 }
}

type ImportedCogoApplicationSnapshot = {
  sourceName?: string
  bundle: CogoApplicationBundle
}

function openImportedCogoApplicationDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IMPORTED_COGO_APPLICATION_DB, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(IMPORTED_COGO_APPLICATION_STORE)) {
        db.createObjectStore(IMPORTED_COGO_APPLICATION_STORE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Failed to open imported Coogo application database'))
  })
}

async function writeImportedCogoApplicationSnapshot(
  projectId: string,
  snapshot: ImportedCogoApplicationSnapshot,
): Promise<void> {
  const db = await openImportedCogoApplicationDb()
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(IMPORTED_COGO_APPLICATION_STORE, 'readwrite')
      transaction.objectStore(IMPORTED_COGO_APPLICATION_STORE).put(snapshot, projectId)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error ?? new Error('Failed to persist imported Coogo application snapshot'))
      transaction.onabort = () => reject(transaction.error ?? new Error('Imported Coogo application snapshot write aborted'))
    })
  } finally {
    db.close()
  }
}

async function readImportedCogoApplicationSnapshot(projectId: string): Promise<ImportedCogoApplicationSnapshot | null> {
  const db = await openImportedCogoApplicationDb()
  try {
    return await new Promise<ImportedCogoApplicationSnapshot | null>((resolve, reject) => {
      const transaction = db.transaction(IMPORTED_COGO_APPLICATION_STORE, 'readonly')
      const request = transaction.objectStore(IMPORTED_COGO_APPLICATION_STORE).get(projectId)
      request.onsuccess = () => resolve((request.result as ImportedCogoApplicationSnapshot | undefined) ?? null)
      request.onerror = () => reject(request.error ?? new Error('Failed to read imported Coogo application snapshot'))
      transaction.onerror = () => reject(transaction.error ?? new Error('Failed to read imported Coogo application snapshot'))
    })
  } finally {
    db.close()
  }
}

async function readStaticImportedCogoApplicationSnapshot(projectId: string): Promise<ImportedCogoApplicationSnapshot | null> {
  try {
    const { buildStaticFigmaImportedCogoApplicationSnapshot } = await import('@/features/cogo-xyz/cogo-xyz-figma-import')
    const snapshot = buildStaticFigmaImportedCogoApplicationSnapshot(projectId)
    if (!snapshot) return null
    return {
      sourceName: snapshot.sourceName,
      bundle: snapshot.bundle as unknown as CogoApplicationBundle,
    }
  } catch (error) {
    console.warn(`[project-slice] failed to build static imported app snapshot for ${projectId}:`, error)
    return null
  }
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

export function bindLoadedAppStudioProjectId(
  app: LoadedApp,
  projectId: string,
): LoadedApp {
  const record = app as unknown as Record<string, unknown>
  return {
    ...app,
    metadata: {
      ...(readRecord(record.metadata) ?? {}),
      studioProjectId: projectId,
    },
  } as unknown as LoadedApp
}

type StudioGenerationEvidenceModel = Parameters<typeof withStudioEndUserAppGenerationProof>[0]

function readProjectBlockGenerationEvidence(
  project: Record<string, unknown>,
): StudioGenerationEvidenceModel | null {
  const blocks = readRecord(project.blocks)
  if (!blocks) return null
  for (const block of Object.values(blocks)) {
    const blockRecord = readRecord(block)
    const meta = readRecord(blockRecord?.metadata) ?? readRecord(blockRecord?.meta)
    const generationEvidence = readRecord(meta?.generationEvidence)
    if (generationEvidence) return generationEvidence as unknown as StudioGenerationEvidenceModel
  }
  return null
}

function readBrowserEvidenceActionFlowTrace(
  browserEvidence: StudioEndUserAppBrowserEvidence,
): ActionFlowTraceEntry | null {
  const evidenceRecord = readRecord(browserEvidence)
  const trace = readRecord(
    evidenceRecord?.actionFlowTraceEvidence ??
      evidenceRecord?.actionFlowBrowserTrace ??
      evidenceRecord?.actionFlowTrace,
  )
  if (trace?.contractVersion !== 'actionflow-trace.v1') return null
  if (trace.status !== 'pass' && trace.status !== 'error') return null
  return trace as unknown as ActionFlowTraceEntry
}

function readDeviceHint(value: unknown): Breakpoint | null {
  if (value === 'mobile' || value === 'tablet' || value === 'desktop') return value
  return null
}

/**
 * The frame a project OPENS in.
 *
 * Precedence, highest first:
 *   1. the device this user picked FOR THIS PROJECT, restored from
 *      `cogo-studio-ui-v1` (`previewDeviceChoices`);
 *   2. an explicit device declared by the project's own settings/manifest;
 *   3. authoritative generated page/source viewport geometry;
 *   4. the app's own kind — an expo-router application is a phone
 *      application and opens in the phone frame
 *      (`breakpointForSourceAppKind`);
 *   5. desktop.
 *
 * Steps 3 and 4 repair two measured defaults without inspecting generated
 * content heuristically: a 375x812 generated page carries enough geometry to
 * open as mobile, while an expo-router app still supplies a mobile default
 * when it has no page/source viewport. The persisted choice remains first so
 * either derived default can be explicitly overridden by the user.
 */
function resolveProjectBreakpoint(
  meta: ProjectMeta | null | undefined,
  manifest: unknown,
  settings: Record<string, unknown> | null | undefined,
  deviceChoice: { choices: Record<string, unknown> | null | undefined; projectId: string | null | undefined },
): Breakpoint {
  const manifestRecord = readRecord(manifest)
  const manifestStudio = readRecord(manifestRecord?.studio)
  const manifestSourceConversion = readRecord(manifestRecord?.sourceConversion)
  const settingsStudio = readRecord(settings?.studio)
  const settingsSourceConversion = readRecord(settings?.sourceConversion)

  return (
    readPersistedDeviceChoice(deviceChoice.choices, deviceChoice.projectId) ??
    readDeviceHint(settingsStudio?.defaultDevice) ??
    readDeviceHint(settingsStudio?.defaultViewport) ??
    readDeviceHint(manifestStudio?.defaultDevice) ??
    readDeviceHint(manifestStudio?.defaultViewport) ??
    readDeviceHint(settingsSourceConversion?.defaultDevice) ??
    readDeviceHint(manifestSourceConversion?.defaultDevice) ??
    (meta?.app_target === 'mobile' || settingsSourceConversion?.mobileOnly === true || manifestSourceConversion?.mobileOnly === true
      ? 'mobile'
      : null) ??
    generatedAppViewportBreakpoint(
      resolveGeneratedAppViewportMetadata(
        { manifest, app_target: meta?.app_target },
        null,
      ),
    ) ??
    breakpointForSourceAppKind(manifestRecord?.kind) ??
    'desktop'
  )
}

/** The device this user last picked for this project, if any. */
function readPersistedDeviceChoice(
  choices: Record<string, unknown> | null | undefined,
  projectId: string | null | undefined,
): Breakpoint | null {
  if (!projectId) return null
  return readDeviceHint(choices?.[projectId])
}

function resolveEditorAppTarget(meta: ProjectMeta | null | undefined): 'mobile' | 'web' | 'desktop' {
  return meta?.app_target === 'mobile' ? 'mobile' : 'web'
}

/**
 * F11 — a block save halted by a typed 409: another session modified the
 * block since this client last observed it. Distinct from saveError (which
 * means "retryable transport/server fault, payload re-queued"): a conflict
 * is NOT re-queued and only resolves through the user's reload. The
 * dismissal guard treats an unresolved conflict as unsaved divergence.
 */
export interface BlockSaveConflict {
  projectId: string
  blocks: Array<{
    blockId: string
    /** The fields whose edit was discarded (e.g. ['flow', 'events']). */
    fields: string[]
    /** The row's current server updated_at when the 409 reported it. */
    serverUpdatedAt: string | null
  }>
}

export type AgenticUxTransactionPhase =
  | 'creating-project'
  | 'project-created'
  | 'project-opened'
  | 'route-committed'
  | 'chat-mounted'
  | 'chat-ready'
  | 'spec-mutating'
  | 'render-committed'
  | 'failed'

export interface AgenticUxDestination {
  domain: StudioAuthorityDomain
  authorityView: string
  railItem: RailItemId
  projectId?: string
  targetRef?: string
  selection?: IdeSelectionContext
}

export interface AgenticUxTransaction {
  id: string
  kind: 'project-create-to-live-render' | 'chat-spec-live-render'
  status: 'running' | 'committed' | 'failed'
  phase: AgenticUxTransactionPhase
  projectId: string | null
  route: string | null
  reference: string | null
  projectName: string | null
  lastSpec: string | null
  error: string | null
  destination?: AgenticUxDestination
  startedAt: number
  updatedAt: number
}

function isLocalProjectFallbackEnabled(): boolean {
  return import.meta.env.VITE_STUDIO_LOCAL_PROJECT_FALLBACK === 'true'
}

/**
 * One piece of work offered as a pickable row. Carries the id, because the
 * whole point of the row is that the surface never has to resolve a typed
 * name back to a project.
 */
export interface PendingWorkChoice {
  id: string
  name: string
  /** `YYYY-MM-DD`, or null when the row had no parsable date. Never invented. */
  updatedOn: string | null
}

export interface ProjectSlice {
  view: StudioView
  setView: (view: StudioView) => void

  currentProject: LoadedApp | null
  currentProjectId: string | null
  projectSource: ProjectSource | null
  projectMeta: ProjectMeta | null
  projectPlan: StudioProjectPlan | null
  /**
   * The open project's `studio_projects.settings` row, verbatim and untyped.
   *
   * It is a SIBLING of `currentProject`, not a field on it, because that is
   * exactly how it arrives: `loadProject` returns `{ app, settings }` and
   * `toLoadedApp` never emits a `settings` key (`LoadedApp` declares none).
   * Readers that reached for `currentProject.settings` were reading
   * `undefined` in production — carry it here, next to `projectMeta` /
   * `projectPlan`, which are loaded from the same response.
   *
   * Consumers must go through a validating reader (e.g.
   * `readAsIsSnapshotFromSettings`, `readIntakeDecisionsFromSettings`) —
   * this is untrusted server data, never a typed contract.
   * `null` for every non-server project path (template, local draft, import,
   * ABox deeplink), which have no settings row.
   */
  projectSettings: Record<string, unknown> | null
  projectList: TemplateSummary[]
  recentProjectIds: string[]

  // Chat entry flow
  pendingChatMessage: string | null
  setPendingChatMessage: (msg: string | null) => void
  pendingChatNotice: string | null
  setPendingChatNotice: (msg: string | null) => void
  /**
   * The exact prompt string most recently handed to
   * `startAppGenerationFromPrompt` — set at the call site, not derived from
   * `pendingChatMessage`/`projectPlan`, so it is a record of what was
   * actually SENT rather than what the composer happened to be holding.
   * `presentGenerationReadinessInChat` also writes the pre-confirm draft
   * here the moment the readiness panel is presented, so a slice/e2e test
   * can compare the value before and after the panel's generate click and
   * observe whether confirming an intake actually changed what will be
   * generated (Studio slice-1, step ①; see
   * `e2e/studio/slice-1-form-app-journey.spec.ts`).
   */
  lastSubmittedPrompt: string | null
  setLastSubmittedPrompt: (prompt: string | null) => void
  /**
   * Work the user may pick, when a chat request resolved to a QUESTION rather
   * than an action — today only "update existing work" with several distinct
   * projects.
   *
   * It exists because the answer to that question had nowhere to land.
   * Measured in production 2026-08-04: the client asked "Which one should I
   * open?", the user typed a project name back, and that reply matched no
   * client detector, so it reached the gateway as free text and the model
   * answered "let me load the app" in prose with no tool call — 36s later the
   * route was still /studio. Rendering the choices as rows removes the parse
   * step entirely: the client asked the question, so the client owns the
   * answer, and each row carries the id it needs.
   */
  pendingWorkChoice: PendingWorkChoice[] | null
  setPendingWorkChoice: (choices: PendingWorkChoice[] | null) => void
  agenticUxTransaction: AgenticUxTransaction | null
  beginAgenticUxTransaction: (input: {
    kind: AgenticUxTransaction['kind']
    reference?: string | null
    projectName?: string | null
    phase?: AgenticUxTransactionPhase
    destination?: AgenticUxDestination
  }) => string
  advanceAgenticUxTransaction: (
    id: string,
    patch: Partial<Omit<AgenticUxTransaction, 'id' | 'kind' | 'startedAt'>>,
  ) => void
  failAgenticUxTransaction: (id: string, error: unknown) => void
  clearAgenticUxTransaction: (id?: string) => void

  // Persistence state
  userProjects: ProjectMeta[]
  isLoadingUserProjects: boolean
  userProjectsError: string | null
  /**
   * When `userProjects` last came back from the server, or `null` when this
   * principal's list has never been read in this session.
   *
   * Screens use it to tell "no saved work" apart from "not read yet" without
   * each keeping its own `loading` flag and firing its own request — that
   * divergence is what put seven concurrent `/api/projects` reads on one
   * screen (see `loadUserProjects`).
   */
  userProjectsLoadedAt: number | null
  blockIdMap: Record<string, string>  // block_name → DB UUID
  isDirty: boolean
  /**
   * A user/editor mutation has diverged from the last verified generated
   * artifact receipt. Normal auto-save persists bytes but does not mint a
   * generated-artifact child receipt, so `markSaved` deliberately preserves
   * this flag until an authenticated project reload re-evaluates the ledger.
   */
  hasUncommittedArtifactLineage: boolean
  isSaving: boolean
  /** Direct document writes bypass the generic auto-save queue. Keep their
   * in-flight state project-scoped so the product never labels a live write
   * as unsaved. */
  documentWorkspaceSaveCounts: Record<string, number>
  saveError: string | null
  /** Advances for every global save-state transition that can supersede an
   * in-flight direct workspace persistence request. */
  saveStateVersion: number
  /** Unresolved optimistic-concurrency conflict (F11) — separate channel
   * from saveError; cleared only by the conflict reload or a project
   * open/close (which replaces the diverged state wholesale). */
  saveConflict: BlockSaveConflict | null
  /** CS-10 — true while resolveSaveConflictByReload is running, so the banner
   * can disable its Reload button (a second click would launch a concurrent
   * reload). Set/cleared exclusively by resolveSaveConflictByReload. */
  isResolvingConflict: boolean
  lastSavedAt: Date | null

  // Publish state
  publishUrl: string | null
  isPublishing: boolean
  /**
   * L5-C3 — true once the live deployment has been TAKEN DOWN this session
   * (deleteDeployedApp resolved) and a fresh publish has NOT re-established it.
   * The slug persists on the project record (the project stays editable), so
   * the slug-derived live URL alone cannot tell the surface a deployment is
   * gone; this flag is the deployment-removed signal the surface reads to STOP
   * advertising the (now-dead) <slug>.cogo.xyz. It is cleared on a fresh
   * publish and on a project open (server truth). It is NOT cleared by
   * refreshDeployedState on a stale 'published' record, because whether the
   * worker flips the Supabase record off 'published' is unverified from
   * cogo-web — the take-down is the authoritative local truth until a publish
   * re-establishes a deployment.
   */
  deploymentTakenDown: boolean
  /** Set by the version-history take-down path the instant deleteDeployedApp
   * resolves (the deployment IS gone) so the publish surface clears its
   * live-URL affordances independent of the unverified server status flip. */
  markDeploymentTakenDown: () => void

  // Template detail
  detailTemplateId: string | null
  openTemplateDetail: (id: string) => void
  closeTemplateDetail: () => void

  // Template actions (existing)
  initProjectList: () => void
  openProject: (id: string) => Promise<void>
  closeProject: () => void

  // ABox deeplink action (Phase 033 Plan 02 / CLOSE-01 — ADDITIVE; the
  // non-ABox openProject/loadTemplate/openServerProject paths are untouched).
  openAboxApp: (loaded: LoadedApp, appId: string) => void

  // Portable Coogo application bundle actions.
  importCogoApplication: (
    bundle: unknown,
    sourceName?: string,
    options?: { preserveOpenPencilAuthority?: boolean },
  ) => Promise<void>
  restoreImportedCogoApplication: (projectId: string) => Promise<boolean>
  exportCurrentCogoApplication: () => CogoApplicationBundle | null

  // D-046-08 (Phase 046 Plan 02 — ADDITIVE) — clear Preview to a neutral
  // empty state after a FAILED ABox deeplink load. Lighter than closeProject:
  // it nulls currentProject + the project-identity fields ONLY — it does NOT
  // reset `view` to 'dashboard', does NOT reset `isDirty`, and does NOT rewrite
  // the URL. PreviewFrameContent then renders the "No project loaded"
  // placeholder = the neutral empty state, with no stale prior render.
  clearProjectToEmpty: () => void

  // Server actions (new)
  /**
   * Read this principal's saved work into `userProjects`.
   *
   * Concurrent callers share ONE request and a recent read is reused for
   * `USER_PROJECTS_FRESH_MS` — mount effects across the dashboard may call
   * this as often as they re-render without multiplying server reads.
   *
   * Pass `{ force: true }` when the answer must be re-read from the server:
   * after a mutation (create / duplicate / rename) and for an explicit user
   * retry. A forced read ignores both the in-flight share and the freshness
   * window, so it can never observe pre-mutation data.
   */
  loadUserProjects: (options?: { force?: boolean }) => Promise<void>
  openServerProject: (
    projectId: string,
    options?: { navigate?: boolean; activateWorkspace?: boolean },
  ) => Promise<void>
  createServerProject: (
    name: string,
    templateId?: string,
    options?: CreateServerProjectOptions,
  ) => Promise<string>
  deleteProject: (projectId: string) => Promise<void>
  duplicateProject: (projectId: string, newName: string) => Promise<string>
  markDirty: () => void
  markSaved: () => void
  markSaveFailed: (message: string) => void
  beginDocumentWorkspaceSave: (projectId: string) => void
  endDocumentWorkspaceSave: (projectId: string) => void
  markSaveConflict: (conflict: BlockSaveConflict) => void
  clearSaveConflict: () => void
  applyEndUserBrowserEvidence: (browserEvidence: StudioEndUserAppBrowserEvidence) => void
  applyActionFlowBrowserTraceEvidence: (trace: ActionFlowTraceEntry) => void
  persistCurrentPublishEvidence: () => Promise<boolean>
  /**
   * Record whether this project's preview follows its source viewport.
   *
   * The preview has honoured `manifest.studio.viewportPolicy` since the policy
   * landed; until now nothing could write it, so every project fell to the
   * absent-policy default and stayed pinned.
   */
  declareViewportPolicy: (policy: ViewportPolicy) => Promise<boolean>

  // Project meta actions
  renameProject: (name: string) => Promise<void>
  updateProjectMeta: (updates: { description?: string; icon?: string }) => Promise<void>
  publishCurrentProject: () => Promise<PublicationReleaseProjection | undefined>
  /**
   * Re-fetch ONLY the project record's meta from the server and reconcile the
   * local `projectMeta` + `publishUrl` to server truth — WITHOUT the heavy,
   * view-resetting reload `openServerProject` performs (no view/dirty/sidebar
   * mutation, no editor re-seed). Used after a deployment take-down
   * (deleteDeployedApp) so the publish surface reflects whatever the server
   * actually did, rather than a blind local status flip. F22 (Lane 5).
   */
  refreshDeployedState: () => Promise<void>
  /**
   * NON-DESTRUCTIVELY re-read the latest persisted publish-readiness evidence
   * from the server and reconcile ONLY the evidence fields onto the in-memory
   * `currentProject`. Unlike `openServerProject`, it does NOT wholesale-replace
   * `currentProject`, does NOT reset `view`/`viewMode`/`isDirty`, and does NOT
   * clear a pending `saveConflict` — so unsaved edits and an unresolved
   * conflict survive a re-check. This is what the publish/deploy "Re-check
   * readiness" affordance calls (CA-2/ST-2, Lane 5): the gate is derived from
   * `currentProject` every render, so refreshing the persisted evidence makes
   * the gate re-evaluate against server truth WITHOUT discarding the creator's
   * work. Returns true when the evidence was refreshed, false when there is no
   * loadable server project (local/absent).
   */
  refreshPublishEvidence: () => Promise<boolean>
  /**
   * NON-DESTRUCTIVELY re-read project-backed proof records from the server and
   * reconcile ONLY `currentProject.metadata.proofRecords` / legacy
   * `libraryProofRecords`. This keeps unsaved UI/data/flow edits and conflict
   * state intact while Deploy/Library can refresh durable readback proof.
   */
  refreshProofRecords: () => Promise<boolean>
}

function loadRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_PROJECTS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveRecentIds(ids: string[]): void {
  try {
    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(ids))
  } catch { /* ignore */ }
}

function createAgenticUxTransactionId(): string {
  const cryptoRandom = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  return `agentic-ux-${cryptoRandom}`
}

function readErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? 'Unknown error')
}

function toLocalSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40)
}

function isLocalProjectId(projectId: string): boolean {
  return projectId.startsWith('local-')
}

const PUBLISH_EVIDENCE_MANIFEST_KEYS = [
  'generationEvidence',
  'productionEvidenceChain',
  'ontologyRetrievalTrace',
  'actionFlowCompilerProof',
  'actionFlowRuntimeSimulationProof',
  'actionFlowReadiness',
  'renderEngineRegistrySummary',
  'actionflowContractSummary',
  'fullCoverageAudit',
  'exhaustiveProofAudit',
  'uiOntologyGenerationConstraints',
  'uiOntologyConformance',
] as const

/**
 * The publish-readiness gate (`evaluateStudioPublishReadiness`) is derived
 * purely from these persisted-evidence fields, read off `currentProject`'s
 * manifest + metadata on every render. A non-destructive "Re-check readiness"
 * (publish-gate-rows.tsx) re-fetches the server bundle and copies ONLY these
 * fields onto the in-memory project — it must NEVER wholesale-replace
 * `currentProject`, so unsaved block/data/flow edits, `isDirty`, and a pending
 * `saveConflict` all survive the re-check (CA-2). Superset of the readiness
 * evaluator's deep-read keys (`generatedAppReadinessGate`, `publishReadinessGate`,
 * `rendererTargetMatrix`, `generationEvidence`, the audit fields) plus the
 * `PUBLISH_EVIDENCE_MANIFEST_KEYS` packaging summaries.
 */
const PUBLISH_READINESS_EVIDENCE_KEYS = [
  ...PUBLISH_EVIDENCE_MANIFEST_KEYS,
  'generatedAppReadinessGate',
  'publishReadinessGate',
  'rendererTargetMatrix',
  'studioFullCoverageAudit',
  'studioExhaustiveProofAudit',
  'datasourceReadinessSidecar',
] as const

const PROJECT_META_READINESS_EVIDENCE_KEYS = [
  ...PUBLISH_READINESS_EVIDENCE_KEYS,
  'studioProjectId',
  'datasourceReadinessImportQuarantine',
] as const

const PROOF_RECORD_METADATA_KEYS = [
  'proofRecords',
  'libraryProofRecords',
] as const

/**
 * Field-scoped evidence merge: returns a shallow clone of `current` whose
 * `manifest` + `metadata` carry the latest readiness-evidence fields from
 * `fresh`, with EVERY other field of `current` (blocks/data/flows + manifest
 * content the creator may have edited) preserved untouched. Removing an evidence
 * key on the server also clears it locally (so a regressed audit can re-block).
 */
function mergePublishReadinessEvidence(
  current: LoadedApp,
  fresh: LoadedApp,
): LoadedApp {
  const currentRecord = current as unknown as Record<string, unknown>
  const freshRecord = fresh as unknown as Record<string, unknown>
  const currentManifest = (current.manifest ?? {}) as unknown as Record<string, unknown>
  const freshManifest = (fresh.manifest ?? {}) as unknown as Record<string, unknown>
  const currentMetadata = (currentRecord.metadata ?? {}) as Record<string, unknown>
  const freshMetadata = (freshRecord.metadata ?? {}) as Record<string, unknown>

  const manifest: Record<string, unknown> = { ...currentManifest }
  const metadata: Record<string, unknown> = { ...currentMetadata }
  for (const key of PUBLISH_READINESS_EVIDENCE_KEYS) {
    // Mirror server truth EXACTLY for evidence keys (set when present, delete
    // when the server no longer reports it) so the gate can both unblock and
    // re-block — but only ever for these evidence keys, never content.
    if (key in freshManifest) manifest[key] = freshManifest[key]
    else delete manifest[key]
    if (key in freshMetadata) metadata[key] = freshMetadata[key]
    else delete metadata[key]
  }

  return {
    ...current,
    manifest: manifest as unknown as LoadedApp['manifest'],
    metadata,
  } as unknown as LoadedApp
}

function mergeProjectMetaReadinessEvidence(
  current: Record<string, unknown> | undefined,
  fresh: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const metadata: Record<string, unknown> = { ...(current ?? {}) }
  const freshMetadata = fresh ?? {}
  for (const key of PROJECT_META_READINESS_EVIDENCE_KEYS) {
    if (key in freshMetadata) metadata[key] = freshMetadata[key]
    else delete metadata[key]
  }
  return Object.keys(metadata).length > 0 ? metadata : undefined
}

function mergeProjectProofRecords(
  current: LoadedApp,
  fresh: LoadedApp,
): LoadedApp {
  const currentRecord = current as unknown as Record<string, unknown>
  const freshRecord = fresh as unknown as Record<string, unknown>
  const currentMetadata = (currentRecord.metadata ?? {}) as Record<string, unknown>
  const freshMetadata = (freshRecord.metadata ?? {}) as Record<string, unknown>
  const metadata: Record<string, unknown> = { ...currentMetadata }

  for (const key of PROOF_RECORD_METADATA_KEYS) {
    if (key in freshMetadata) metadata[key] = freshMetadata[key]
    else delete metadata[key]
  }

  return {
    ...current,
    metadata,
  } as unknown as LoadedApp
}

/** Cross-slice fields that project actions need to mutate directly. */
interface CrossSliceFields {
  viewMode: 'preview' | 'editor' | 'code'
  designSystem: DesignSystemState
  setDesignSystemBulk: (options: Partial<DesignSystemState>) => void
  // IdeV2Slice fields written by project actions (P3 wiring)
  currentProjectName: string | undefined
  currentProjectSlug: string | undefined
  projectLoadError: string | null
  studioMode: import("../shell/ide-v2/types").StudioMode
  setStudioMode: (mode: import("../shell/ide-v2/types").StudioMode) => void
  viewport: Breakpoint
  viewportPreview: Breakpoint
  openPencilFigmaSource: OpenPencilFigmaSourceBundle | null
  openPencilSourceWorkspace: OpenPencilSourceWorkspace | null
  projectDesignSystemRegistration: ReturnType<
    typeof readProjectDesignSystemRegistrationFromSettings
  >
}

/**
 * Members owned by another slice that project actions reach for through
 * `get()`. `user` is read by `loadUserProjects` so a cached list can never be
 * reused across a change of principal. `clearDispatch` is ide-v2-slice's own
 * run-scoped reset, INVOKED (never reimplemented) by `closeProject` — see the
 * comment there for why closing has to run it.
 */
interface CrossSliceReads {
  user: { id?: string } | null
  session: { access_token?: string } | null
  clearDispatch: () => void
  /** UiSlice — the persisted per-project preview device (see ui-slice.ts). */
  previewDeviceChoices: Record<string, ViewportSize>
}

type Set = (fn: (state: ProjectSlice & CrossSliceFields) => void) => void
type Get = () => ProjectSlice & CrossSliceReads

function clearOpenPencilAuthorityForProjectReplacement(
  state: ProjectSlice & CrossSliceFields,
  nextProjectId: string,
  preserve = false,
): void {
  if (preserve || state.currentProjectId === nextProjectId) return
  state.openPencilFigmaSource = null
  state.openPencilSourceWorkspace = null
  // R1 — the registered design system is evidence about ONE project's source.
  // It must die with the source it describes, or generation in the next
  // project could bind a design system derived from a file that project never
  // imported. (The bind-time drift check would also reject it, but leaving a
  // foreign record in state to be caught later is a trap, not a design.)
  state.projectDesignSystemRegistration = null
}

function clearExecutableChatConfirmationsForProjectReplacement(
  state: ProjectSlice & CrossSliceFields,
  nextProjectId: string,
): void {
  if (state.currentProjectId === nextProjectId) return
  // Confirmation cards are executable proposals bound to the project that
  // created them. Keep ordinary conversation history, but never carry an old
  // Confirm button into another project where it could appear actionable even
  // though the capability gateway will reject the stale scope.
  //
  // I2: a terminology-selection card is the same class of stale-scope risk
  // — picking one of its candidates declares/writes a terminology scope
  // (terminology-scope-selection.ts) onto whichever project is open AT
  // CLICK TIME, not the project that was open when the card was appended.
  // Purge it here too, same as capability-confirmation.
  //
  // Plan B1 Task 4: a template-proposal card is the same stale-target risk
  // — its Accept button calls instantiateTemplateToProject + openServerProject
  // using whichever project happens to be open at click time, and its
  // Decline button starts free generation into that same current context.
  // Neither action is safe to leave attached to a project the user has
  // since navigated away from, so purge it on project switch too.
  //
  // Task 5.5: a generation-readiness panel is the same class once more —
  // its report was computed against the project open at SUBMIT time, and
  // its generate button would start generation into whichever project is
  // open at CLICK time. A stale panel carrying another project's verdict
  // must not survive the switch.
  const stateWithChat = state as typeof state & {
    chatMessages?: Array<{ kind?: string }>
  }
  stateWithChat.chatMessages = (stateWithChat.chatMessages ?? []).filter(
    (message) =>
      message.kind !== 'capability-confirmation' &&
      message.kind !== 'terminology-selection' &&
      message.kind !== 'template-proposal' &&
      message.kind !== 'generation-readiness',
  )
}

/**
 * Slice-2 T6 — the RENDER of the run being left behind, released on a project
 * switch. Called only from `openServerProject`, and only when a DIFFERENT
 * project is already open (the caller owns that guard; at project BIRTH
 * `currentProjectId` is null and nothing here may run, or the generation's own
 * progress frame and rendered output would be nulled as the IDE opens).
 *
 * MEASURED, before this existed (probe commit `2d5fded36`, the ko walk):
 * switching from project A to project B left A's `pageProgressFrame`
 * (`app-generation-progress-frame`) and A's `dispatchedOutput` (`rendered`) in
 * the store, on BOTH reachable switch routes — the picker row and the
 * `?projectId=` deeplink. `dispatchedOutput` is not inert there: with A's value
 * carried, project B's build canvas renders project A's app.
 * `build-mode-canvas.tsx:274` binds `const out = useStudioStore((s) =>
 * s.dispatchedOutput)`; `:350-353` reaches B's own preview ONLY on the null
 * arm — `if (!out) { return currentProjectPreview }` — and `:410-412` otherwise
 * renders `manifest={out.manifest}` / `blocks={out.blocks}`, which are A's.
 *   NOT the empty-state gate, and the first draft of this comment said it was.
 *   `IDEShellLayout.tsx:201-205` is `hasBuildCanvas = studioMode === 'build' &&
 *   activeRailItem === 'preview' && (dispatchedOutput !== null || currentProject
 *   !== null)`; after a switch `currentProject` is B's app, so that gate does
 *   NOT turn on the carried field and a maintainer checking it would conclude
 *   this clear is unnecessary. The behaviour claim is true by the canvas, not
 *   by the gate.
 *
 * DELIBERATELY NOT `clearDispatch()`, and the difference is the point.
 * `closeProject` invokes that action to release the WHOLE run (T9); a switch
 * must not, because this codebase has already decided — in the sibling
 * function above, and in three unit tests named "…but keeps conversation
 * history" — that ordinary transcript SURVIVES a project switch while only
 * executable cards are purged. Reversing that is a product decision about what
 * a switch means, not a clear; it is left where slice 2's design doc put it, as
 * a known cell. What is released here is what no declared intent keeps: the
 * previous project's rendered output and its in-flight progress card.
 *
 * ALSO NOT RELEASED, each for a stated reason rather than by omission:
 *   · `chatComposerDraft` — ide-v2-slice.ts:132 declares the opposite in so
 *     many words ("Unsent composer text survives responsive panel unmounts and
 *     workspace switches").
 *   · `lastSubmittedPrompt` — it carries (measured), but it has no reader
 *     anywhere in `src/`: the only matches are its type (:382), its initial
 *     value (:884), its setter (:885) and the setter's two callers. Changing
 *     state nothing reads is not a fix.
 *   · `chatRailChip` — same stale-scope class, but this journey never selects
 *     an element, so it was never armed and never measured. Unmeasured state
 *     is not cleared on a hunch.
 */
function releaseRunRenderForProjectReplacement(
  state: ProjectSlice & CrossSliceFields,
): void {
  const stateWithRender = state as typeof state & {
    dispatchedOutput?: unknown
    pageProgressFrame?: unknown
  }
  stateWithRender.dispatchedOutput = null
  stateWithRender.pageProgressFrame = null
}

export function createProjectSlice(
  set: Set,
  get: Get,
  _api: unknown,
): ProjectSlice {
  if (api && 'setProjectWorkerSessionAccessTokenResolver' in api) {
    api.setProjectWorkerSessionAccessTokenResolver(
      () => get().session?.access_token ?? null,
    )
  }
  return {
    view: 'dashboard',
    setView: (view) => set((s) => { s.view = view }),

    currentProject: null,
    currentProjectId: null,
    projectSource: null,
    projectMeta: null,
    projectPlan: null,
    projectSettings: null,
    projectList: [],
    recentProjectIds: loadRecentIds(),

    // Chat entry flow
    pendingChatMessage: null,
    setPendingChatMessage: (msg) => set((s) => { s.pendingChatMessage = msg }),
    pendingChatNotice: null,
    setPendingChatNotice: (msg) => set((s) => { s.pendingChatNotice = msg }),
    lastSubmittedPrompt: null,
    setLastSubmittedPrompt: (prompt) => set((s) => { s.lastSubmittedPrompt = prompt }),
    pendingWorkChoice: null,
    setPendingWorkChoice: (choices) => set((s) => { s.pendingWorkChoice = choices }),
    agenticUxTransaction: null,
    beginAgenticUxTransaction: (input) => {
      const id = createAgenticUxTransactionId()
      const now = Date.now()
      set((s) => {
        s.agenticUxTransaction = {
          id,
          kind: input.kind,
          status: 'running',
          phase: input.phase ?? 'creating-project',
          projectId: null,
          route: null,
          reference: input.reference ?? null,
          projectName: input.projectName ?? null,
          lastSpec: null,
          error: null,
          destination: input.destination,
          startedAt: now,
          updatedAt: now,
        }
      })
      return id
    },
    advanceAgenticUxTransaction: (id, patch) => set((s) => {
      const current = s.agenticUxTransaction
      if (!current || current.id !== id || current.status !== 'running') return
      s.agenticUxTransaction = {
        ...current,
        ...patch,
        status: patch.phase === 'render-committed' ? 'committed' : (patch.status ?? current.status),
        updatedAt: Date.now(),
      }
    }),
    failAgenticUxTransaction: (id, error) => set((s) => {
      const current = s.agenticUxTransaction
      if (!current || current.id !== id) return
      s.agenticUxTransaction = {
        ...current,
        status: 'failed',
        phase: 'failed',
        error: readErrorMessage(error),
        updatedAt: Date.now(),
      }
    }),
    clearAgenticUxTransaction: (id) => set((s) => {
      if (!id || s.agenticUxTransaction?.id === id) {
        s.agenticUxTransaction = null
      }
    }),

    // Persistence state
    userProjects: [],
    isLoadingUserProjects: false,
    userProjectsError: null,
    userProjectsLoadedAt: null,
    blockIdMap: {},
    isDirty: false,
    hasUncommittedArtifactLineage: false,
    isSaving: false,
    documentWorkspaceSaveCounts: {},
    saveError: null,
    saveStateVersion: 0,
    saveConflict: null,
    isResolvingConflict: false,
    lastSavedAt: null,

    // Publish state
    publishUrl: null,
    isPublishing: false,
    deploymentTakenDown: false,

    markDeploymentTakenDown: () => set((s) => {
      s.deploymentTakenDown = true
      // A removed deployment is no longer served — drop any cached publish URL
      // too, so nothing in the app advertises the dead deployment.
      s.publishUrl = null
    }),

    // Template detail
    detailTemplateId: null,

    openTemplateDetail: (id: string) => set((s) => {
      if (s.projectList.length === 0) {
        s.projectList = getTemplateCatalog()
      }
      s.detailTemplateId = id
      s.view = 'template-detail'
    }),

    closeTemplateDetail: () => {
      set((s) => {
        s.detailTemplateId = null
        s.view = 'dashboard'
      })
    },

    // -- Template actions (existing, kept for backward compat) --

    initProjectList: () => set((s) => {
      s.projectList = getTemplateCatalog()
    }),

    openProject: async (id: string) => {
      try {
        const loaded = await loadTemplate(id)
        useFlowEditorStore.getState().ensureProject(id)
        set((s) => {
          clearOpenPencilAuthorityForProjectReplacement(s, id)
          s.currentProject = bindLoadedAppStudioProjectId(loaded as LoadedApp, id)
          s.projectSettings = null
          s.currentProjectId = id
          s.projectSource = 'template'
          s.projectMeta = null
          s.projectPlan = null
          s.view = 'workspace'
          s.isDirty = false
          s.viewMode = 'preview';
          (s as any).chatSidebarOpen = true;
          (s as any).inspectorPanelOpen = false;

          const recents = [id, ...s.recentProjectIds.filter((r) => r !== id)].slice(0, MAX_RECENT)
          s.recentProjectIds = recents
          saveRecentIds(recents)
        })

        // Phase 039-02 (D-039-4b) — page-editor store bridge. `openProject` is
        // the local-template open action (the 4 callers are all `?template=`
        // deeplink / studio-bridge paths — server projects use the separate
        // `openServerProject`). The IDE v2 Pages surface renders
        // `PageEditorShell`, whose `useEditorStore` is a SEPARATE store from
        // this studio store. `EditorShell`'s mount-effect skips its own
        // `loadApp` whenever `currentProjectId` is set ("server project — owner
        // is StudioShell"), so a deeplinked template left the page-editor store
        // empty ("No template loaded" / "0 pages"). Bridge the loaded template
        // into `useEditorStore` here so the Pages surface renders it. Scoped to
        // the template-open path only — `openServerProject` is untouched.
        try {
          const { useEditorStore } = await import('@/features/page-editor/store/editor-store')
          useEditorStore.getState().loadApp(loaded as LoadedApp)
        } catch (bridgeErr) {
          console.warn(`[project-slice] page-editor bridge failed for ${id}:`, bridgeErr)
        }

        // Auto-populate story from template data (per STUDIO-UX-01)
        // Priority: 1) localStorage persisted story, 2) TEMPLATE_STORIES, 3) fallback via useEffect
        const STORY_KEY = `cogo-story-${id}`
        let restoredStory: Story | null = null
        try {
          const saved = localStorage.getItem(STORY_KEY)
          if (saved) restoredStory = JSON.parse(saved)
        } catch { /* ignore parse errors */ }

        if (restoredStory) {
          const { useStudioStore } = await import('@/store/studio-store')
          useStudioStore.getState().setStory(restoredStory)
        } else {
          const templateStory = TEMPLATE_STORIES[id]
          if (templateStory) {
            const { useStudioStore } = await import('@/store/studio-store')
            useStudioStore.getState().setStory(templateStory)
          }
          // If no TEMPLATE_STORIES match, the useEffect in StoryContentCanvas
          // will call projectToStory() as fallback
        }
      } catch (err) {
        console.error(`[project-slice] Failed to open template ${id}:`, err)
      }
    },

    closeProject: () => {
      const closingProjectId = get().currentProjectId
      if (
        typeof window !== 'undefined' &&
        /^\/ide\/import-[^/]+(\/|$)/.test(window.location.pathname)
      ) {
        return
      }
      // Flush edits still inside the debounce window. Pending saves carry
      // the projectId captured at queue time, so completing after the
      // state below clears is safe — the PATCH targets the closed project.
      const closeFlush = flushSaves()
      if (closingProjectId) {
        clearSessionOnlyDesignMutationAuthority(closingProjectId)
      }
      useFlowEditorStore.getState().ensureProject(null)
      set((s) => {
        s.currentProject = null
        s.projectSettings = null
        s.currentProjectId = null
        s.projectSource = null
        s.projectMeta = null
        s.projectPlan = null
        s.currentProjectName = undefined
        s.currentProjectSlug = undefined
        // `projectLoadError` is NOT cleared here: `clearDispatch()` below
        // owns it (ide-v2-slice.ts, same action, same tick). It used to be
        // set in both places; measured by mutation (T9, M7a), deleting this
        // one changed nothing — two producers for one field means a later
        // reader cannot tell which one is load-bearing.
        s.blockIdMap = {}
        s.openPencilFigmaSource = null
        s.openPencilSourceWorkspace = null
        s.projectDesignSystemRegistration = null
        s.view = 'dashboard'
        s.isDirty = false
        // F11 — closing abandons the diverged in-memory state; an unresolved
        // conflict has nothing left to resolve against.
        s.saveConflict = null
        // Wave 5 Lane C (slice-5A §A5-a / R2) — the model above is only half
        // of what a project owns. WHERE THE USER WAS inside it — the data
        // navigator's category/selection/filter, the active rail surface, the
        // chat's authority context, the IDE's chat drawer — was left pinned to
        // the closed project, and the measured cost was that creating a SECOND
        // project became impossible with no error shown. Released in the SAME
        // transaction as the model, so no reader can observe one without the
        // other. See `project-session-exit.ts` for what is cleared, what is
        // not, and which half is derived rather than listed.
        resetProjectSessionSurfaceOnExit(
          s as unknown as ProjectSessionSurfaceState,
        )
      })
      // Slice-1 re-entry (T9) — the run's state does not all live in this
      // slice, and closing is the ONE transition every path back to the
      // dashboard passes through (the account menu / logo / TopBar / the
      // load-failure screen's own button / the popstate handler for
      // `/studio`), i.e. the point where the next run begins. Both stores
      // below are already project-scoped; neither was being released here,
      // so the next run started on top of the previous one.
      //
      // Measured before this line existed: after closing, the dashboard
      // still showed the closed project's whole chat transcript — including
      // an executable capability-confirmation card with a live Confirm
      // button — its finished generation-progress card, its rendered
      // `dispatchedOutput`, and (from `useEditorStore`) its app, which the
      // chat panel reads back as "Selected target: App · Preview · <title>".
      // Run 1's ordinary messages then survived INTO run 2's IDE: 13
      // messages where a clean second run has 5.
      //
      // `clearDispatch` is ide-v2-slice's existing reset for exactly this
      // set (its own comment says a stale chip "would attach stale
      // refinementContext to the next run"); until now its only caller was
      // the "Start over" branch of a FAILED chat turn
      // (chat-messages.tsx:1771) — a failure handler that has to remember to
      // clean up, which is why nothing was cleaned up on the success path.
      // It is invoked, not reimplemented, so there is one definition of what
      // a run leaves behind.
      //
      // `useEditorStore.reset()` is the page-editor twin of the
      // `useFlowEditorStore.ensureProject(null)` already above; no
      // production code called it before. Run AFTER the state above is
      // cleared, so the auto-save bridge's editor subscription
      // (auto-save-bridge.ts:634-647) sees `app === null` and a null
      // `currentProjectId` and cannot queue an empty snapshot against the
      // project being closed.
      useEditorStore.getState().reset()
      get().clearDispatch()
      // Slice 6 §2.3b — the DURABLE carrier the in-memory reset above cannot
      // reach: `localStorage['cogo-studio:cogo-xyz-registry:v1']` held the
      // closed project's raw prompt at the dashboard and across a full page
      // reload, read back by four dashboard pages. Sign-out hygiene existed
      // (`clearOnSignOut`, auth-slice); this is its project-exit counterpart.
      // A storage side effect, so it runs HERE with the other side effects,
      // not inside the store transaction — `project-session-exit.ts` owns
      // in-memory position only, and its header says why. The project's
      // durable registry lives in `project.settings.cogoXyzRegistry`, so
      // this removal loses nothing a project open cannot restore.
      cogoXyzRegistry.clearOnProjectExit()
      // Baselines mirror blockIdMap's lifecycle (F11) — but the close-flush
      // above reads its If-Match tokens at DISPATCH time, which is ALWAYS
      // after this synchronous body (the dispatch sits behind awaits/the
      // single-flight latch), so a synchronous clear here was not "a race":
      // it deterministically stripped every close-time PATCH of its token —
      // an unprotected LWW write on a primary exit path (PI-L2-06). Defer
      // the drop until the flush settles, scope it to the CLOSED project
      // (other projects' pending saves keep their tokens — see
      // replaceProjectBlockBaselines), and skip it when the user re-opened
      // the same project meanwhile: the load path's
      // replaceProjectBlockBaselines owns the fresh tokens then.
      void closeFlush
        .catch(() => {})
        .then(() => {
          if (closingProjectId && get().currentProjectId !== closingProjectId) {
            dropProjectBlockBaselines(closingProjectId)
          }
        })
      const dashboardReturnPath =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/studio/')
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : '/studio'
      window.history.replaceState(null, '', dashboardReturnPath)
      window.dispatchEvent(new PopStateEvent('popstate'))
    },

    // -- Server actions (new) --

    loadUserProjects: async (options) => {
      const force = options?.force === true
      // The list is per-principal. Anything cached for a different user id —
      // or for the signed-out state — must never be reused after a switch.
      const ownerKey = get().user?.id ?? null
      if (ownerKey !== userProjectsLoad.ownerKey) {
        userProjectsLoad = {
          ownerKey,
          inFlight: null,
          loadedAt: 0,
          erroredAt: 0,
        }
        set((s) => {
          s.userProjectsLoadedAt = null
        })
      }

      const now = Date.now()
      if (!force) {
        // Same request already on the wire — join it instead of opening a
        // second one. This is what collapses the dashboard's simultaneous
        // mount effects into a single `/api/projects` read.
        if (userProjectsLoad.inFlight) return userProjectsLoad.inFlight
        if (
          userProjectsLoad.loadedAt > 0 &&
          now - userProjectsLoad.loadedAt < USER_PROJECTS_FRESH_MS
        ) {
          return
        }
        // A failure that repeats on every re-render is a request storm, not a
        // recovery. Unforced retries wait out a short cooldown; the user's
        // own "Try again" is forced and never waits.
        if (
          userProjectsLoad.erroredAt > 0 &&
          now - userProjectsLoad.erroredAt < USER_PROJECTS_ERROR_COOLDOWN_MS
        ) {
          return
        }
      }

      // The load state this request belongs to. A change of principal REPLACES
      // `userProjectsLoad` wholesale, so a read still on the wire when that
      // happens belongs to nobody: landing it would write the previous
      // principal's list AND stamp it as the new principal's fresh cache, so
      // the new principal's own mount would skip its read and keep looking at
      // the wrong list for the whole freshness window. Comparing object
      // identity — not the id string — also covers a switch back to the same
      // principal, which starts a genuinely new cache.
      const owned = userProjectsLoad
      /**
       * True once this request's principal is no longer the current one.
       * Such a result is dropped; the loading flag is only cleared when no
       * newer read is running, so the screen can never be left on a skeleton
       * that nothing will resolve.
       */
      const orphaned = (): boolean => {
        if (userProjectsLoad === owned) return false
        if (!userProjectsLoad.inFlight) set((s) => { s.isLoadingUserProjects = false })
        return true
      }

      const request = (async () => {
        set((s) => {
          s.isLoadingUserProjects = true
          s.userProjectsError = null
        })
        try {
          const projects = await api.listProjects()
          if (orphaned()) return
          const loadedAt = Date.now()
          owned.loadedAt = loadedAt
          owned.erroredAt = 0
          set((s) => {
            s.userProjects = projects
            s.isLoadingUserProjects = false
            s.userProjectsLoadedAt = loadedAt
          })
        } catch (err) {
          console.error('[project-slice] Failed to load user projects:', err)
          if (orphaned()) return
          owned.erroredAt = Date.now()
          set((s) => {
            s.isLoadingUserProjects = false
            s.userProjectsError =
              err instanceof Error && err.message
                ? err.message
                : 'Could not load saved work.'
          })
        }
      })()

      owned.inFlight = request
      try {
        await request
      } finally {
        if (owned.inFlight === request) {
          owned.inFlight = null
        }
      }
    },

    // ADDITIVE — Phase 033 Plan 02 / CLOSE-01 (D-CLOSE01-A). Writes a
    // LoadedApp reverse-transformed from `cogo.application_ontology` (by
    // `createAboxLoadedAppLoader`, Plan 033-01) into `currentProject`.
    // Mirrors the `openServerProject` write idiom (currentProject + view +
    // viewMode + Preview-mode start) WITHOUT touching that action — the
    // 254-suite + non-ABox loads stay byte-identical (R-033-03). `appId` is
    // the deeplink's `?app_id=` (used as `currentProjectId` so reload /
    // recent-projects keying behaves like the server path).
    openAboxApp: (loaded: LoadedApp, appId: string) => {
      useFlowEditorStore.getState().ensureProject(appId)
      set((s) => {
        clearOpenPencilAuthorityForProjectReplacement(s, appId)
        s.currentProject = bindLoadedAppStudioProjectId(loaded, appId)
        s.projectSettings = null
        s.currentProjectId = appId
        s.projectSource = 'server'
        s.projectMeta = null
        s.projectPlan = null
        s.currentProjectName = loaded.manifest?.name ?? appId
        s.view = 'workspace'
        s.isDirty = false
        s.viewMode = 'preview';
        (s as any).chatSidebarOpen = true;
        (s as any).inspectorPanelOpen = false;
        s.studioMode = 'edit'
      })

      // Bridge ABox project into Page Editor store
      import('@/features/page-editor/store/editor-store')
        .then(({ useEditorStore }) => {
          useEditorStore.getState().loadApp(loaded)
        })
        .catch((bridgeErr) => {
          console.warn(`[project-slice] page-editor bridge failed for abox app ${appId}:`, bridgeErr)
        })
    },

    importCogoApplication: async (
      bundle: unknown,
      sourceName?: string,
      options?: { preserveOpenPencilAuthority?: boolean },
    ) => {
      const imported = normalizeCogoApplicationBundle(bundle, sourceName)
      const projectId = imported.id
      const defaultBreakpoint = resolveProjectBreakpoint(null, imported.app.manifest, null, {
        choices: get().previewDeviceChoices,
        projectId,
      })
      const studioManifest =
        imported.app.manifest && typeof imported.app.manifest === 'object'
          ? (((imported.app.manifest as unknown) as Record<string, unknown>).studio as Record<string, unknown> | undefined)
          : undefined
      const initialWorkingPageView =
        studioManifest?.initialWorkingPageView === 'report' ? 'report' : 'app'
      const initialWorkingReportKind =
        studioManifest?.initialWorkingReportKind === 'gap-test'
          ? 'gap-test'
          : 'spec-ontology'
      const persistImportedProjectSnapshot = async () => {
        if (
          typeof window === 'undefined' ||
          typeof indexedDB === 'undefined'
        ) {
          return
        }
        try {
          await writeImportedCogoApplicationSnapshot(projectId, {
              sourceName,
              bundle: exportLoadedAppAsCogoApplication(imported.app, { projectId }),
          })
        } catch (error) {
          console.warn(`[project-slice] failed to persist imported app snapshot for ${projectId}:`, error)
        }
      }
      if (typeof window !== 'undefined') {
        await persistImportedProjectSnapshot()
        window.history.pushState(null, '', `/ide/${projectId}`)
      }
      const commitImportedProject = () => {
        useFlowEditorStore.getState().ensureProject(projectId)
        set((s) => {
          clearOpenPencilAuthorityForProjectReplacement(
            s,
            projectId,
            options?.preserveOpenPencilAuthority === true,
          )
          s.currentProject = bindLoadedAppStudioProjectId(imported.app, projectId)
          s.projectSettings = null
          s.currentProjectId = projectId
          s.projectSource = 'import'
          s.projectMeta = null
          s.projectPlan = deriveStudioProjectPlanFromMeta({
            name: imported.name,
            app_target: 'web',
            description: 'Imported Coogo application bundle',
          })
          s.currentProjectName = imported.name
          s.currentProjectSlug = toLocalSlug(imported.name)
          s.blockIdMap = {}
          s.publishUrl = null
          s.deploymentTakenDown = false
          s.projectLoadError = null
          s.saveConflict = null
          s.view = 'workspace'
          s.isDirty = false
          s.viewMode = 'preview';
          (s as any).chatSidebarOpen = true;
          (s as any).inspectorPanelOpen = false;
          s.studioMode = 'edit'
          s.viewport = defaultBreakpoint
          s.viewportPreview = defaultBreakpoint
          ;(s as any).activeRailItem = 'preview'
          ;(s as any).workingPageView = initialWorkingPageView
          ;(s as any).workingReportKind = initialWorkingReportKind
        })
      }
      const bridgeImportedProject = async () => {
        try {
          const { useEditorStore } = await import('@/features/page-editor/store/editor-store')
          const editor = useEditorStore.getState()
          editor.loadApp(imported.app)
          editor.setBreakpoint(defaultBreakpoint)
          editor.setAppTarget(defaultBreakpoint === 'mobile' ? 'mobile' : 'web')
        } catch (bridgeErr) {
          console.warn(`[project-slice] page-editor bridge failed for imported app ${projectId}:`, bridgeErr)
        }
      }
      commitImportedProject()

      await bridgeImportedProject()

      if (typeof window !== 'undefined') {
        const importWindow = window
        importWindow.dispatchEvent(new PopStateEvent('popstate'))
        const ownsImportRoute = () => {
          if (typeof window === 'undefined' || window !== importWindow) return false
          return importWindow.location.pathname === `/ide/${projectId}` || importWindow.location.pathname.startsWith(`/ide/${projectId}/`)
        }
        const restoreIfRouteStillOwnsImport = () => {
          const state = get()
          if (!ownsImportRoute()) return
          if (state.currentProjectId === projectId && state.currentProject) return
          commitImportedProject()
          void bridgeImportedProject()
        }
        importWindow.setTimeout(restoreIfRouteStillOwnsImport, 0)
        importWindow.setTimeout(restoreIfRouteStillOwnsImport, 50)
        importWindow.setTimeout(restoreIfRouteStillOwnsImport, 250)
        // Route loaders may briefly replace the imported project after the
        // pushState event. Reconcile only for a bounded stabilization window;
        // an unbounded interval leaks after navigation and test/browser
        // teardown. The realm guard above also makes the deferred timeouts
        // harmless once their originating window is gone.
        let remainingRouteChecks = 20
        const intervalId = importWindow.setInterval(() => {
          restoreIfRouteStillOwnsImport()
          remainingRouteChecks -= 1
          if (remainingRouteChecks <= 0 || !ownsImportRoute()) {
            importWindow.clearInterval(intervalId)
          }
        }, 100)
      }
    },

    restoreImportedCogoApplication: async (projectId: string) => {
      if (typeof window === 'undefined' || !projectId.startsWith('import-')) return false

      let snapshot: ImportedCogoApplicationSnapshot | null = null
      let snapshotSource: 'indexeddb' | 'static-fallback' = 'indexeddb'
      try {
        snapshot = await readImportedCogoApplicationSnapshot(projectId)
      } catch (error) {
        console.warn(`[project-slice] failed to read imported app snapshot for ${projectId}:`, error)
      }
      if (!snapshot) {
        snapshot = await readStaticImportedCogoApplicationSnapshot(projectId)
        snapshotSource = 'static-fallback'
      }
      if (!snapshot) {
        set((s) => {
          clearOpenPencilAuthorityForProjectReplacement(s, projectId)
          s.currentProject = null
          s.projectSettings = null
          s.currentProjectId = projectId
          s.projectSource = 'import'
          s.projectMeta = null
          s.projectPlan = null
          s.currentProjectName = undefined
          s.currentProjectSlug = undefined
          s.projectLoadError = projectLoadErrorMessage('imported-app-not-in-this-browser')
          s.view = 'workspace'
        })
        return false
      }

      try {
        const imported = normalizeCogoApplicationBundle(snapshot.bundle, snapshot.sourceName)
        if (imported.id !== projectId) {
          set((s) => {
            clearOpenPencilAuthorityForProjectReplacement(s, projectId)
            s.currentProject = null
            s.projectSettings = null
            s.currentProjectId = projectId
            s.projectSource = 'import'
            s.projectLoadError = projectLoadErrorMessage('imported-app-url-mismatch')
            s.view = 'workspace'
          })
          return false
        }
        const defaultBreakpoint = resolveProjectBreakpoint(null, imported.app.manifest, null, {
          choices: get().previewDeviceChoices,
          projectId,
        })
        if (snapshotSource === 'static-fallback') {
          void writeImportedCogoApplicationSnapshot(projectId, snapshot).catch((error) => {
            console.warn(`[project-slice] failed to persist static imported app snapshot for ${projectId}:`, error)
          })
        }

        useFlowEditorStore.getState().ensureProject(projectId)
        set((s) => {
          clearExecutableChatConfirmationsForProjectReplacement(s, projectId)
          clearOpenPencilAuthorityForProjectReplacement(s, projectId)
          s.currentProject = bindLoadedAppStudioProjectId(imported.app, projectId)
          s.projectSettings = null
          s.currentProjectId = projectId
          s.projectSource = 'import'
          s.projectMeta = null
          s.projectPlan = deriveStudioProjectPlanFromMeta({
            name: imported.name,
            app_target: 'web',
            description: 'Imported Coogo application bundle',
          })
          s.currentProjectName = imported.name
          s.currentProjectSlug = toLocalSlug(imported.name)
          s.blockIdMap = {}
          s.publishUrl = null
          s.deploymentTakenDown = false
          s.projectLoadError = null
          s.saveConflict = null
          s.view = 'workspace'
          s.isDirty = false
          s.viewMode = 'preview';
          (s as any).chatSidebarOpen = true;
          (s as any).inspectorPanelOpen = false;
          s.studioMode = 'edit'
          s.viewport = defaultBreakpoint
          s.viewportPreview = defaultBreakpoint
        })

        try {
          const { useEditorStore } = await import('@/features/page-editor/store/editor-store')
          const editor = useEditorStore.getState()
          editor.loadApp(imported.app)
          editor.setBreakpoint(defaultBreakpoint)
          editor.setAppTarget(defaultBreakpoint === 'mobile' ? 'mobile' : 'web')
        } catch (bridgeErr) {
          console.warn(`[project-slice] page-editor bridge failed for restored imported app ${projectId}:`, bridgeErr)
        }
        return true
      } catch (error) {
        console.warn(`[project-slice] failed to restore imported app snapshot for ${projectId}:`, error)
        set((s) => {
          clearOpenPencilAuthorityForProjectReplacement(s, projectId)
          s.currentProject = null
          s.projectSettings = null
          s.currentProjectId = projectId
          s.projectSource = 'import'
          s.projectLoadError = (error as Error).message || 'Failed to restore imported Coogo application snapshot.'
          s.view = 'workspace'
        })
        return false
      }
    },

    exportCurrentCogoApplication: () => {
      const current = get().currentProject
      if (!current) return null
      return exportLoadedAppAsCogoApplication(current, { projectId: get().currentProjectId ?? undefined })
    },

    // ADDITIVE — Phase 046 Plan 02 (D-046-08). Clears Preview to a neutral
    // empty state after a failed ABox deeplink load (called from
    // `abox-app-deeplink.ts`'s `.catch`). A MINIMAL subset of `closeProject`:
    // it nulls `currentProject` + the identity fields so PreviewFrameContent
    // renders its "No project loaded" placeholder — but UNLIKE `closeProject`
    // it does NOT set `view = 'dashboard'`, does NOT reset `isDirty`, and does
    // NOT call `window.history.replaceState` (a failed deeplink should not
    // navigate away or rewrite the URL — it just clears the stale render).
    clearProjectToEmpty: () => {
      const closingProjectId = get().currentProjectId
      if (closingProjectId) {
        clearSessionOnlyDesignMutationAuthority(closingProjectId)
      }
      useFlowEditorStore.getState().ensureProject(null)
      set((s) => {
        s.currentProject = null
        s.projectSettings = null
        s.currentProjectId = null
        s.projectSource = null
        s.projectMeta = null
        s.projectPlan = null
        s.currentProjectName = undefined
        s.currentProjectSlug = undefined
        s.projectLoadError = null
        s.openPencilFigmaSource = null
        s.openPencilSourceWorkspace = null
        s.projectDesignSystemRegistration = null
        // NOTE — intentionally NOT touched (the distinction from closeProject):
        //   s.view          — left as-is (no dashboard reset)
        //   s.isDirty       — left as-is
        //   window.history  — no URL rewrite
      })
    },

    openServerProject: async (
      projectId: string,
      options?: { navigate?: boolean; activateWorkspace?: boolean },
    ) => {
      // Flush the previous project's pending edits before switching —
      // captured projectIds route them to the project they were made in.
      void flushSaves()
      set((s) => { s.projectLoadError = null })
      if (isLocalProjectId(projectId)) {
        const localProject = get().currentProject
        const isCurrentLocalProject = get().currentProjectId === projectId && localProject
        if (isCurrentLocalProject) {
          set((s) => {
            if (options?.activateWorkspace !== false) {
              s.view = 'workspace'
            }
            s.projectLoadError = null
          })
          return
        }
        set((s) => {
          s.projectLoadError = projectLoadErrorMessage('local-draft-not-in-this-browser')
        })
        return
      }
      try {
        const { meta, app, blockIdMap, settings } = await api.loadProject(projectId)
        const restoredDesignSystem = parseDesignSystemState(
          settings?.designSystemState,
        )
        if (restoredDesignSystem) {
          app.tokens = { ...computeThemeTokens(restoredDesignSystem) }
        }
        const defaultBreakpoint = resolveProjectBreakpoint(meta, app.manifest, settings, {
          choices: get().previewDeviceChoices,
          projectId,
        })
        useFlowEditorStore.getState().ensureProject(projectId)
        // Slice-2 T6 — a project switch releases the RENDER of the run it
        // leaves. Computed before the transaction below because the write it
        // guards happens inside it, and `s.currentProjectId` is overwritten
        // there.
        //
        // The guard is NOT `!==` alone. This function is ALSO the project-BIRTH
        // path (`start-app-generation-from-prompt.ts:2082`, `await
        // store.createServerProject(...)`, which awaits this), where
        // `currentProjectId` is null — so a birth satisfies a bare `!==` while a
        // genuine REPLACEMENT does not, and M3 in this task's mutation table
        // shows that is the ONLY input on which the two guards differ.
        //
        // WHY CONSERVATIVE RATHER THAN DEMONSTRATED, corrected after review.
        // This comment first said the generation's progress frame and rendered
        // output "are already in the store" at birth. TRACED, and that is FALSE
        // on the app-generation route: the birth call at
        // `start-app-generation-from-prompt.ts:2082` completes before the
        // generation is even dispatched (`:2421-2425`, `mcpBus.execute({ tool:
        // 'cogo.studio.requestAppGeneration' … })`), and both writes live inside
        // that handler — the progress frame at `register-studio-tools.ts:9016`
        // and `setDispatchedOutput({ kind: 'rendered' … })` at `:9798`. So on
        // the route actually measured, a bare `!==` would have cleared nothing.
        // The narrow guard is kept because it can only ever UNDER-clear, and
        // because a birth-time non-null `dispatchedOutput` is reachable in
        // principle — `use-channel-dispatch.ts:201` calls `setDispatchedOutput`
        // from the build surface, which can be open with `currentProjectId`
        // null. That route is UNMEASURED and is therefore not claimed as the
        // justification; conservatism is.
        //
        // Placed after `api.loadProject` has resolved, so a switch that FAILS to
        // load leaves the run it came from intact instead of emptying the
        // surface behind a "could not load" screen (slice 1's ④ orphan shape).
        const replacedProjectId = get().currentProjectId
        const isProjectReplacement =
          Boolean(replacedProjectId) && replacedProjectId !== projectId
        set((s) => {
          clearExecutableChatConfirmationsForProjectReplacement(s, projectId)
          if (isProjectReplacement) {
            releaseRunRenderForProjectReplacement(s)
          }
          clearOpenPencilAuthorityForProjectReplacement(s, projectId)
          s.currentProject = bindLoadedAppStudioProjectId(app, projectId)
          s.currentProjectId = projectId
          s.projectSource = 'server'
          s.projectMeta = meta
          s.projectPlan = readStudioProjectPlan(settings) ?? meta.studioProjectPlan ?? deriveStudioProjectPlanFromMeta(meta)
          // The whole settings row, carried beside the app rather than folded
          // into it — `toLoadedApp` emits no `settings` key, so this is the
          // ONLY place the row survives the load. Readers (As-Is snapshot,
          // intake decisions, spec-field provenance) re-validate it.
          s.projectSettings = settings ?? null
          s.currentProjectName = meta?.name ?? app?.manifest?.name ?? 'Untitled'
          s.currentProjectSlug = meta?.slug ?? undefined
          s.blockIdMap = blockIdMap
          // R1 — hydrate the project's registered .fig design system from the
          // row that owns it, so generation follows the imported source after
          // a reload / on another device, not only in the tab that registered
          // it. `null` when absent; identity is re-checked at bind time.
          s.projectDesignSystemRegistration =
            readProjectDesignSystemRegistrationFromSettings(settings)
          // F11 — a fresh load IS the conflict resolution: baselines were
          // just re-seeded by api.loadProject and the diverged local state
          // is replaced wholesale.
          s.saveConflict = null
          if (options?.activateWorkspace !== false) {
            s.view = 'workspace'
          }
          s.isDirty = false
          s.hasUncommittedArtifactLineage = false
          // A fresh server open IS server truth for the deployment — clear any
          // stale session-local take-down flag so the surface re-derives live-
          // ness from the reloaded record (L5-C3).
          s.deploymentTakenDown = false
          s.viewMode = 'preview';
          (s as any).chatSidebarOpen = true;
          (s as any).inspectorPanelOpen = false;
          s.studioMode = 'edit'
          s.viewport = defaultBreakpoint
          s.viewportPreview = defaultBreakpoint

          // Restore 6-axis design system state if saved (Phase 403-01)
          if (restoredDesignSystem) {
            // This code is already inside the project-open Immer transaction.
            // Calling the action (which starts a nested set) is overwritten by
            // the outer transaction; assign the restored authority directly.
            s.designSystem = { ...restoredDesignSystem }
          }

          const recents = [projectId, ...s.recentProjectIds.filter((r) => r !== projectId)].slice(0, MAX_RECENT)
          s.recentProjectIds = recents
          saveRecentIds(recents)
        })

        // Bridge server project into Page Editor store so Pages tab hydrates correctly
        try {
          const { useEditorStore } = await import('@/features/page-editor/store/editor-store')
          const editor = useEditorStore.getState()
          editor.loadApp(app as LoadedApp)
          editor.setBreakpoint(defaultBreakpoint)
          editor.setAppTarget(resolveEditorAppTarget(meta))
        } catch (bridgeErr) {
          console.warn(`[project-slice] page-editor bridge failed for server project ${projectId}:`, bridgeErr)
        }

        // F-013 fix — preserve /ide/<projectId>[/<pane>] URLs entered via the
        // Phase 425 sibling route (e.g. via the Apps Overview "Edit in IDE"
        // button). Without this guard, every IDE-URL entry was silently
        // rewritten to /studio?projectId=<id> on mount, breaking
        // bookmarkable IDE deep-links AND cascading into F-014 (DsSurface
        // crash because the surrounding RouterProvider provided by
        // IDEShellWithRouteSync is only on the /ide/* path).
        // Match any /ide/<segment>[/<rest>] regardless of whether the id
        // is encoded — defensive against future param shape changes.
        if (typeof window !== 'undefined' && options?.navigate !== false) {
          const onIdeRoute = /^\/ide\/[^/]+(\/|$)/.test(window.location.pathname)
          if (!onIdeRoute) {
            window.history.pushState(null, '', `/ide/${projectId}`)
            window.dispatchEvent(new PopStateEvent('popstate'))
          }
        }
      } catch (err) {
        console.error(`[project-slice] Failed to open server project ${projectId}:`, err)
        const chrome = COOGO_IDE_CHROME_COPY[getLocale()]
        toast.error(chrome.couldNotLoadProject)
        set((s) => {
          s.projectLoadError = chrome.projectNotOpenNothingToChange
        })
      }
    },

    createServerProject: async (
      name: string,
      templateId?: string,
      options?: CreateServerProjectOptions,
    ) => {
      let template: LoadedApp | undefined
      // Starters/showroom template lineage (see features/templates/
      // starter-lineage.ts): derived from the SAME bundle that seeds the
      // project, so no extra fetches. Null when the served template can't
      // prove an identity — never a placeholder.
      let templateLineage: Awaited<ReturnType<typeof deriveStarterTemplateLineage>> = null
      if (templateId) {
        const loadedTemplate = await loadTemplateWithBundle(templateId)
        template = loadedTemplate.app as LoadedApp
        templateLineage = await deriveStarterTemplateLineage(templateId, loadedTemplate.files)
        if (!templateLineage) {
          console.warn(
            `[project-slice] template "${templateId}" supplied no provable lineage — creating without instantiatedFrom`,
          )
        }
      }

      try {
        const {
          navigate: _navigate,
          activateWorkspace: _activateWorkspace,
          ...projectApiOptions
        } = options ?? {}
        const projectId = await api.createProject(
          name,
          template,
          projectApiOptions,
        )
        // Record template provenance through the SAME writer the registry
        // instantiation path uses (`persistProjectTemplateLineage` — one
        // shape, one key: `settings.cogoApplicationMetadata.instantiatedFrom`).
        // It read-merge-writes the whole settings object, so the birth-write
        // siblings createProject just wrote (`generation`,
        // `libraryStructureSelection`, `designSystemPackRelease`) compose
        // rather than clobber, and a later `markProjectGenerationComplete`
        // read-merge-write preserves this in turn.
        //
        // Awaited (not fire-and-forget as in instantiate-service): the very
        // next statement opens the project and the interaction-capability gate
        // reads the freshly loaded meta, so a racing PATCH would leave the
        // template surfaces hidden until a reload. Non-fatal on failure — the
        // project already exists server-side and losing provenance must never
        // roll it back or surface as a create failure.
        if (templateLineage) {
          try {
            await api.persistProjectTemplateLineage(projectId, templateLineage)
          } catch (lineageErr) {
            console.warn('[project-slice] failed to persist template lineage:', lineageErr)
          }
        }
        // Load the newly created project from server
        await get().openServerProject(projectId, options)
        // Forced: the just-created project is not in any cached read.
        await get().loadUserProjects({ force: true })
        return projectId
      } catch (err) {
        if (!isLocalProjectFallbackEnabled()) {
          const message = err instanceof Error ? err.message : 'Failed to create project'
          // F32 — distinguish a mid-session JWT expiry (project-api.ts throws
          // 'Not authenticated' when the worker JWT is gone) from any other
          // create failure. On expiry the user is routed back to sign-in by the
          // caller, so use the canonical session-expiry copy (mirrors
          // gateway-chat-service.ts AUTH_EXPIRED) instead of the create-error
          // phrasing.
          const authExpired = message === 'Not authenticated'
          const userMessage = authExpired
            ? 'Session expired — please sign in again'
            : 'Failed to create project'
          // F32 — this is the single canonical create-failure toast. `dedupe`
          // keeps it idempotent (a concurrent double-create surfacing the same
          // failure collapses to one) WITHOUT making the store-wide dedup that
          // swallowed distinct same-text failures elsewhere (persona-ux-08).
          toast.error(userMessage, { dedupe: true })
          set((s) => { s.projectLoadError = message })
          // F32 — the throw is now MARKED: it already fired the toast above
          // (alreadyToasted), so the hero catch must NOT stack a second generic
          // toast; authExpired tells the hero to redirect to sign-in.
          throw new ProjectCreateError({ userMessage, authExpired, cause: err })
        }

        // Explicit local fallback for fixture/dev-only runs.
        const localId = `local-${Date.now()}`
        // v2 App shape requires `schemaVersion: "3.0"`; v2 Page shape requires
        // `layout` + `blockLayout`. Synthesized fallback mirrors an empty project.
        const manifest = template?.manifest ?? {
          appId: localId,
          name,
          version: '1.0',
          schemaVersion: '3.0' as const,
          pages: [{
            id: 'home',
            route: '/',
            title: 'Home',
            blocks: [],
            layout: 'stack',
            blockLayout: {},
          }],
          datasources: {},
          globalState: {},
        }
        // Ensure manifest has the project name
        if (!manifest.name) (manifest as any).name = name

        useFlowEditorStore.getState().ensureProject(localId)
        const loadedApp: LoadedApp = {
          manifest,
          blocks: template?.blocks ?? {},
          data: template?.data ?? {},
          flows: template?.flows ?? {},
          tokens: template?.tokens,
          flowDiagram: (template as any)?.flowDiagram,
        }
        set((s) => {
          clearOpenPencilAuthorityForProjectReplacement(s, localId)
          s.currentProject = bindLoadedAppStudioProjectId(loadedApp, localId)
          s.projectSettings = null
          s.currentProjectId = localId
          s.projectSource = 'template'
          s.projectMeta = null
          s.projectPlan = deriveStudioProjectPlanFromMeta({
            name,
            app_target: 'web',
            description: template?.manifest?.name ?? null,
          })
          s.currentProjectName = name
          s.currentProjectSlug = toLocalSlug(name)
          if (options?.activateWorkspace !== false) {
            s.view = 'workspace'
          }
          s.isDirty = false
          // Always start in Preview mode (Lovable-style)
          s.viewMode = 'preview';
          (s as any).chatSidebarOpen = true;
          (s as any).inspectorPanelOpen = false;
        })
        if (typeof window !== 'undefined' && options?.navigate !== false) {
          window.history.pushState(null, '', `/ide/${localId}`)
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
        console.log('[project-slice] Created local project:', name, localId)
        return localId
      }
    },

    deleteProject: async (projectId: string) => {
      await api.archiveProject(projectId)
      set((s) => {
        s.userProjects = s.userProjects.filter((p) => p.id !== projectId)
      })
    },

    duplicateProject: async (projectId: string, newName: string) => {
      const newId = await api.duplicateProject(projectId, newName)
      // Forced: the copy is not in any cached read.
      await get().loadUserProjects({ force: true })
      return newId
    },

    markDirty: () => set((s) => {
      markProjectContentDirty(s)
      s.saveError = null
      s.saveStateVersion += 1
    }),

    markSaved: () => set((s) => {
      // Generic persistence confirms that the current bytes reached project
      // storage; it does not mint a generated-artifact child receipt. Capture
      // the pre-save dirty state before clearing it so every mutation writer
      // (including mirrored/MCP writers) permanently stops claiming the
      // previous generated lineage. Server hydration enters with isDirty=false
      // and therefore remains eligible for its verified durable receipt.
      if (s.isDirty) s.hasUncommittedArtifactLineage = true
      s.isDirty = false
      s.isSaving = false
      s.saveError = null
      s.lastSavedAt = new Date()
      s.saveStateVersion += 1
    }),

    markSaveFailed: (message: string) => set((s) => {
      s.isDirty = true
      s.isSaving = false
      s.saveError = message
      s.saveStateVersion += 1
    }),

    beginDocumentWorkspaceSave: (projectId: string) => set((s) => {
      if (s.currentProjectId !== projectId) return
      s.documentWorkspaceSaveCounts[projectId] =
        (s.documentWorkspaceSaveCounts[projectId] ?? 0) + 1
    }),

    endDocumentWorkspaceSave: (projectId: string) => set((s) => {
      const nextCount = Math.max(
        0,
        (s.documentWorkspaceSaveCounts[projectId] ?? 0) - 1,
      )
      if (nextCount === 0) {
        delete s.documentWorkspaceSaveCounts[projectId]
      } else {
        s.documentWorkspaceSaveCounts[projectId] = nextCount
      }
    }),

    // F11 — record a conflict-halted save. Merges per blockId when conflicts
    // accumulate for the same project (e.g. a second edit to a still-stale
    // block 409s again) so the surface lists each block once with its latest
    // server token. A conflict for a DIFFERENT project replaces the record —
    // the banner always describes the currently relevant divergence.
    // NOTE markSaved deliberately does NOT clear this: a later successful
    // save of an unrelated block must not dismiss the unresolved conflict.
    markSaveConflict: (conflict: BlockSaveConflict) => set((s) => {
      // L2-COR-06 / CS-6 — cross-project guard: a 409 from project A settling
      // after the user switched to project B must NOT raise a contextless
      // banner in B (whose Reload would yank the workspace back to A). The
      // edit is already halted server-side (the 409 rejected it; the stale
      // payload is not re-queued), so there is nothing the user can act on
      // from B — drop the record and log it. The banner copy is single-block
      // and project-anonymous, so scoping it to the current project is the
      // simpler defensible fix than threading a project name into the UI.
      if (s.currentProjectId != null && conflict.projectId !== s.currentProjectId) {
        console.warn(
          `[project-slice] Dropping cross-project save conflict for ${conflict.projectId} (current project ${s.currentProjectId}) — halted server-side, not actionable from here`,
        )
        return
      }
      s.isDirty = true
      s.isSaving = false
      s.saveStateVersion += 1
      const existing = s.saveConflict
      if (existing && existing.projectId === conflict.projectId) {
        const byBlock = new Map(existing.blocks.map((b) => [b.blockId, b]))
        for (const incoming of conflict.blocks) {
          const prior = byBlock.get(incoming.blockId)
          byBlock.set(incoming.blockId, prior
            ? {
                blockId: incoming.blockId,
                fields: Array.from(new Set([...prior.fields, ...incoming.fields])),
                serverUpdatedAt: incoming.serverUpdatedAt ?? prior.serverUpdatedAt,
              }
            : incoming)
        }
        s.saveConflict = { projectId: conflict.projectId, blocks: Array.from(byBlock.values()) }
      } else {
        s.saveConflict = conflict
      }
    }),

    clearSaveConflict: () => set((s) => {
      s.saveConflict = null
    }),

    applyEndUserBrowserEvidence: (browserEvidence: StudioEndUserAppBrowserEvidence) => set((s) => {
      const project = s.currentProject as unknown as Record<string, unknown> | null
      if (!project) return
      const manifest = project.manifest as Record<string, unknown> | undefined
      const metadata = project.metadata as Record<string, unknown> | undefined
      const generationEvidence = (
        metadata?.generationEvidence ??
        manifest?.generationEvidence ??
        readProjectBlockGenerationEvidence(project) ??
        buildStudioGenerationEvidence(manifest ?? project)
      ) as StudioGenerationEvidenceModel | undefined
      if (!generationEvidence) return
      const proof = generationEvidence.endUserAppProof
      const now = Date.now()
      const hasValidationEvidence = Boolean(
        browserEvidence.validationResult ||
          browserEvidence.blockQualityScores ||
          browserEvidence.validationRagEvidence,
      )
      const baseProof = !proof || hasValidationEvidence
        ? buildStudioEndUserAppGenerationProof({
            evidence: generationEvidence,
            app: project,
            startedAtMs: now,
            completedAtMs: now,
            validationDurationMs: 0,
            validationResult: browserEvidence.validationResult,
            blockQualityScores: browserEvidence.blockQualityScores,
            browserEvidence,
            validationRagEvidence: browserEvidence.validationRagEvidence ?? proof?.validationRag,
          })
        : proof

      const upgradedProof = applyStudioEndUserBrowserEvidenceToProof({
        evidence: generationEvidence,
        app: project,
        proof: baseProof,
        browserEvidence,
      })
      const upgradedEvidence = withStudioEndUserAppGenerationProof(generationEvidence, upgradedProof)
      const projectWithEvidence = attachStudioGenerationEvidenceToApp(project, upgradedEvidence) as Record<string, unknown>
      const upgradedManifest = readRecord(projectWithEvidence.manifest)
      let nextProject = {
        ...projectWithEvidence,
        ...(upgradedManifest
          ? {
              manifest: withApplicationGenerationQualityFromProof(
                upgradedManifest as ApplicationGenerationQualitySource,
                {
                  browserEvidence,
                  endUserProof: upgradedProof,
                },
              ),
            }
          : {}),
      } as unknown as LoadedApp
      const actionFlowTrace = readBrowserEvidenceActionFlowTrace(browserEvidence)
      if (actionFlowTrace) {
        nextProject = withActionFlowBrowserTraceReadiness(nextProject, actionFlowTrace)
      }
      s.currentProject = nextProject
      markProjectContentDirty(s)
      s.saveStateVersion += 1
    }),

    applyActionFlowBrowserTraceEvidence: (trace: ActionFlowTraceEntry) => set((s) => {
      if (!s.currentProject) return
      s.currentProject = withActionFlowBrowserTraceReadiness(
        s.currentProject as LoadedApp,
        trace,
      )
    }),

    persistCurrentPublishEvidence: async () => {
      const projectId = get().currentProjectId
      const current = get().currentProject
      if (!projectId || isLocalProjectId(projectId) || !current?.manifest) return false

      const manifest = api.withServerDatasourceReadinessManifestAuthority(
        current.manifest,
        get().projectMeta,
      )
      await api.saveProjectMeta(projectId, { manifest })
      set((s) => {
        if (s.currentProjectId === projectId && s.currentProject) {
          s.currentProject.manifest = manifest as never
        }
      })
      return true
    },

    declareViewportPolicy: async (policy: ViewportPolicy) => {
      const projectId = get().currentProjectId
      const current = get().currentProject
      if (!projectId || isLocalProjectId(projectId) || !current) return false

      // Read-modify-write of the whole manifest, which is how it is saved.
      // `manifestWithViewportPolicy` carries every sibling across; replacing
      // `studio` wholesale would destroy the source frames the pin reads.
      const manifest = manifestWithViewportPolicy(
        (current as { manifest?: unknown }).manifest,
        policy,
      )
      const readinessSafeManifest = api.withServerDatasourceReadinessManifestAuthority(
        manifest,
        get().projectMeta,
      )
      await api.saveProjectMeta(projectId, { manifest: readinessSafeManifest })
      set((s) => {
        if (s.currentProjectId !== projectId) return
        const project = s.currentProject as { manifest?: unknown } | null
        if (project) project.manifest = readinessSafeManifest
      })
      return true
    },

    // -- Project meta actions --

    renameProject: async (name: string) => {
      const projectId = get().currentProjectId
      if (!projectId) return
      const operation = Symbol(`rename:${projectId}`)
      latestProjectRename.set(projectId, operation)
      try {
        await api.saveProjectMeta(projectId, { name })

        // A successful PATCH is only an acknowledgement that the write request
        // completed. Read the project back through an independent authenticated
        // GET before updating any local mirrors so Studio never reports a rename
        // that the server authority cannot reproduce.
        const readback = await api.getProject(projectId)
        if (!readback) {
          throw new Error(`Project rename readback failed for "${projectId}"`)
        }
        if (readback.id !== projectId || readback.name !== name) {
          throw new Error(
            `Project rename readback mismatch for "${projectId}": expected "${name}"`,
          )
        }
        if (get().currentProjectId !== projectId) {
          throw new Error(
            `Project changed before rename readback completed for "${projectId}"`,
          )
        }
        if (latestProjectRename.get(projectId) !== operation) {
          throw new Error(
            `Project rename was superseded before readback completed for "${projectId}"`,
          )
        }

        set((s) => {
          if (s.projectMeta) s.projectMeta.name = name
          s.currentProjectName = name
          if (s.currentProject?.manifest) {
            s.currentProject.manifest.name = name
          }
        })
      } finally {
        if (latestProjectRename.get(projectId) === operation) {
          latestProjectRename.delete(projectId)
        }
      }
    },

    updateProjectMeta: async (updates: { description?: string; icon?: string }) => {
      const projectId = get().currentProjectId
      if (!projectId) return
      await api.saveProjectMeta(projectId, updates)
      set((s) => {
        if (s.projectMeta) {
          if (updates.description !== undefined) s.projectMeta.description = updates.description
        }
        if (updates.description !== undefined && s.currentProject?.manifest) {
          ;(s.currentProject.manifest as unknown as Record<string, unknown>).description = updates.description
        }
      })
    },

    publishCurrentProject: async () => {
      const projectId = get().currentProjectId
      if (!projectId) return
      // Dynamic import — publish-gate.ts (and everything it pulls in:
      // content-form/project-lineage.ts, content-form/template-ai-client.ts)
      // statically imports `@/store/studio-store` itself, so a static import
      // here would close the same studio-store -> project-slice ->
      // publish-gate -> studio-store cycle `auto-save.ts`'s
      // `projectIdSource` comment documents (and this file already avoids
      // the analogous editor-store cycle the same way, five times over,
      // below).
      const { runTemplatePublishGate, TemplatePublishGateBlockedError } =
        await import('@/features/templates/publish-gate')
      set((s) => { s.isPublishing = true })
      try {
        await flushEditorFullPersist()
        await flushSaves()
        const savedProject = get().currentProject
        if (savedProject?.manifest) {
          const manifest = api.withServerDatasourceReadinessManifestAuthority(
            savedProject.manifest,
            get().projectMeta,
          )
          await api.saveProjectMeta(projectId, { manifest })
        }
        // Plan B3 Task 3 — STRICT fail-closed publication judge gate. A
        // non-instance project (the majority) resolves `{allowed:true}` with
        // zero judge/network calls, so this is byte-equivalent to the
        // pre-Task-3 publish path for everyone else. A blocked instance
        // throws below, which the catch block (already the sole
        // isPublishing-reset + toast path) handles.
        const gate = await runTemplatePublishGate(projectId)
        if (!gate.allowed) {
          throw new TemplatePublishGateBlockedError(gate)
        }
        const release = await preparePublicationCandidate(projectId, {
          targetProfileId: 'target-profile.cogo-runtime-web.v1',
          // This path prepares the FIRST candidate from the saved project and
          // holds no release projection, so it has no server-projected
          // ontology identity to relay. `undefined` is the honest answer: the
          // browser never authors these refs, and binding the first candidate
          // for an ontology-bound project is the server's job. The field is a
          // required key precisely so this had to be said rather than omitted.
          applicationOntology: undefined,
        })
        set((s) => {
          s.isPublishing = false
        })
        toast.success('Publication candidate prepared. Open Launch to deploy it.')
        return release
      } catch (err) {
        set((s) => { s.isPublishing = false })
        toast.error(
          err instanceof TemplatePublishGateBlockedError
            ? err.messages[0]
            : 'Failed to prepare publication candidate',
        )
        throw err
      }
    },

    refreshDeployedState: async () => {
      const projectId = get().currentProjectId
      if (!projectId || isLocalProjectId(projectId)) return
      // Re-fetch server truth for the project record only. We deliberately do
      // NOT mutate view/isDirty/currentProject here (that is openServerProject's
      // job) — this is a meta-only reconciliation after a take-down. The slug
      // persists on the project record, so the deployment-removed signal lives
      // in the `deploymentTakenDown` flag (set by markDeploymentTakenDown the
      // instant the delete resolved); here we just realign the status pill +
      // version + publishUrl to whatever the server now reports.
      //
      // CA-4 — this runs as `void refreshDeployedState()` from the surface, so
      // it MUST NOT reject: a transient 5xx/network blip on this re-fetch would
      // otherwise become an unhandled promise rejection. The take-down itself
      // already succeeded (deleteDeployedApp resolved, deploymentTakenDown is
      // set), so on a refresh failure we keep the deployment-removed state and
      // surface a humanized toast — we never roll the take-down back, and we
      // never throw.
      try {
        const { meta } = await api.loadProject(projectId)
        set((s) => {
          if (s.projectMeta?.id === projectId) {
            s.projectMeta.status = meta.status
            s.projectMeta.version = meta.version
            s.projectMeta.updated_at = meta.updated_at
          }
          // If the server flipped the record off 'published', drop the cached
          // publish URL so the UI stops advertising a removed deployment.
          if (meta.status !== 'published') s.publishUrl = null
        })
      } catch {
        // The deployment IS gone (the delete succeeded); the local
        // deploymentTakenDown flag already reflects that, so the surface stays
        // truthful. We only failed to re-read the rest of the record.
        toast.error("Took down the live site. Couldn't refresh the project status — reload to see the latest.")
      }
    },

    refreshPublishEvidence: async () => {
      const projectId = get().currentProjectId
      const current = get().currentProject
      if (!projectId || isLocalProjectId(projectId) || !current) return false
      // Re-fetch the server bundle ONLY to read its persisted readiness
      // evidence — NOT to reload the project. We deliberately do NOT touch
      // view/viewMode/isDirty/saveConflict/blockIdMap or the page-editor store
      // here (that is openServerProject's destructive job): the gate is derived
      // from currentProject every render, so copying just the evidence fields
      // makes it re-evaluate against server truth while the creator's unsaved
      // edits and any pending conflict survive (CA-2/ST-2).
      const { app, meta } = await api.loadProject(projectId)
      set((s) => {
        if (!s.currentProject || s.currentProjectId !== projectId) return
        s.currentProject = mergePublishReadinessEvidence(
          s.currentProject as LoadedApp,
          app as LoadedApp,
        )
        if (s.projectMeta?.id === projectId) {
          const metadata = mergeProjectMetaReadinessEvidence(
            s.projectMeta.metadata,
            meta.metadata,
          )
          if (metadata) s.projectMeta.metadata = metadata
          else delete s.projectMeta.metadata
          s.projectMeta.datasourceReadinessManifestSidecar =
            meta.datasourceReadinessManifestSidecar ?? null
          s.projectMeta.datasourceReadinessManifestLocations =
            meta.datasourceReadinessManifestLocations
        }
      })
      return true
    },

    refreshProofRecords: async () => {
      const projectId = get().currentProjectId
      const current = get().currentProject
      if (!projectId || isLocalProjectId(projectId) || !current) return false

      const { app } = await api.loadProject(projectId)
      set((s) => {
        if (!s.currentProject || s.currentProjectId !== projectId) return
        s.currentProject = mergeProjectProofRecords(
          s.currentProject as LoadedApp,
          app as LoadedApp,
        )
      })
      return true
    },
  }
}
